import React from 'react';
import { Button, Checkbox, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { type Task } from '../../stores/TaskStore';
import { type Tag } from '../../stores/TagsStore';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useTaskDetailStore } from '@/stores/TaskDetailStore';
import NormalTag from '../ui/Tag';

interface DraggableTaskItemProps {
  task: Task;
  taskTags: Tag[];
  onToggle: (taskId: string) => void;
  onDelete: (taskId: string) => void;
  onTogglePin: (taskId: string) => void;
}

export const DraggableTaskItem: React.FC<DraggableTaskItemProps> = ({
  task,
  taskTags,
  onToggle,
  onDelete,
  onTogglePin,
}) => {
  const taskDetailStore = useTaskDetailStore();
  const t = useTranslations('DraggableTaskItem');
  const elementRef = useRef<HTMLDivElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleOpen = () => {
    taskDetailStore.setTaskId(task.id);
  };

  useEffect(() => {
    const element = elementRef.current;
    const dragHandle = dragHandleRef.current;
    if (!element || !dragHandle) return;

    return draggable({
      element,
      dragHandle,
      getInitialData: () => ({ taskId: task.id }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });
  }, [task.id]);

  return (
    <div
      ref={elementRef}
      className={`
        flex items-center gap-3 p-1 rounded-lg
        relative overflow-hidden transition-all duration-300 ease-out
        ${isDragging
          ? 'opacity-75 scale-[0.98] shadow-xl'
          : 'hover:shadow-sm'
        }
      `}
      style={{
        background: task.isPinned
          ? 'linear-gradient(to right, var(--color-primary-light), var(--color-card))'
          : 'var(--color-card)',
        transform: isDragging ? 'rotate(1deg) scale(0.98)' : 'none',
        boxShadow: isDragging ? '0 10px 25px rgba(229, 95, 51, 0.15)' : undefined,
        border: '1px solid var(--color-border)',
      }}
      onClick={() => {
        // 避免从 Dialog 的 portal 冒泡回来时重新打开
        if (isDragging) return;
        handleOpen();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        // 不要拦截来自可编辑区域/输入控件的按键（例如 Dialog 中的 MDXEditor）
        const target = e.target as HTMLElement;
        if (
          target.closest('input, textarea, [contenteditable="true"], [role="textbox"]')
        ) {
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (!isDragging) handleOpen();
        }
      }}
    >
      <div
        className={`absolute left-0 top-0 bottom-0 rounded-r-sm transition-all duration-300 ease-out ${task.isPinned ? 'w-1 opacity-100' : 'w-0 opacity-0'
          }`}
        style={{ background: 'var(--color-primary)' }}
      />
      <div
        ref={dragHandleRef}
        className={`transition-colors duration-200 cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
        style={{
          color: isDragging ? 'var(--color-primary)' : 'var(--color-muted-foreground)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DragIndicatorIcon sx={{ fontSize: '1.2rem' }} />
      </div>

      <Checkbox
        checked={task.completed}
        onClick={(e) => e.stopPropagation()}
        onChange={() => onToggle(task.id)}
        sx={{
          color: 'var(--color-button-checkbox)',
          '&.Mui-checked': {
            color: 'var(--color-button-checkbox)',
          },
        }}
      />

      <div className={`flex-1 space-y-1 transition-opacity duration-200 ${isDragging ? 'opacity-90' : 'opacity-100'
        }`}>
        <Typography
          className={task.completed ? 'line-through' : ''}
          sx={{
            wordBreak: 'break-word',
            lineHeight: 1.4,
            whiteSpace: 'pre-wrap',
            color: task.completed ? 'var(--color-muted-foreground)' : 'var(--color-foreground)',
          }}
        >
          {task.title}
        </Typography>
        {taskTags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {taskTags.map((tag) => (
              <NormalTag key={tag.id} tag={tag} />
            ))}
          </div>
        )}
      </div>

      <Button
        onClick={(e) => {
          e.stopPropagation();
          onTogglePin(task.id);
        }}
        sx={{
          width: 32,
          height: 32,
          minWidth: 32,
          borderRadius: '50%',
          color: task.isPinned ? 'var(--color-button-pin-active)' : 'var(--color-button-pin)',
          backgroundColor: task.isPinned ? 'var(--color-primary-light)' : 'transparent',
          '&:hover': {
            color: 'var(--color-button-pin-active)',
            backgroundColor: task.isPinned ? 'var(--color-primary-light)' : 'var(--color-primary-light)',
          },
          transition: 'all 0.2s ease',
          transform: isDragging ? 'scale(0.95)' : 'scale(1)',
          opacity: isDragging ? 0.7 : 1,
        }}
        title={task.isPinned ? t('unpin') : t('pinTask')}
      >
        {task.isPinned ? (
          <PushPinIcon sx={{ fontSize: '1.1rem' }} />
        ) : (
          <PushPinOutlinedIcon sx={{ fontSize: '1.1rem' }} />
        )}
      </Button>

      <Button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(task.id);
        }}
        sx={{
          width: 32,
          height: 32,
          minWidth: 32,
          borderRadius: '50%',
          color: 'var(--color-button-delete)',
          '&:hover': {
            backgroundColor: 'var(--color-primary-light)',
          },
          transition: 'all 0.2s ease',
          transform: isDragging ? 'scale(0.95)' : 'scale(1)',
          opacity: isDragging ? 0.7 : 1,
        }}
      >
        <DeleteIcon sx={{ fontSize: '1.1rem' }} />
      </Button>

    </div>
  );
};
